import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { hasRole, useAuth } from "@/lib/auth";
import { usePersona } from "@/lib/persona";

/**
 * Gate component for the authenticated `/app/*` subtree.
 *
 * - Loading: skeleton, no flash.
 * - Unauthenticated: redirect to `/login` (current subdomain's login page).
 * - Authenticated, but missing the persona's required role: redirect to `/`
 *   with a soft message. (User installed the partner PWA but is actually an
 *   attendee — common, not a security failure.)
 * - Authorized: render children.
 *
 * The persona is implicit (derived from hostname) — no need to pass `role`.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const persona = usePersona();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  const returnToRef = useRef<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Preserve return path so post-login bounces back here.
      // NOTE 1: TanStack Router's `location.search` is a parsed OBJECT — coercing it
      // to string via `+` throws "Cannot convert object to primitive value".
      // Use window.location.search (native browser API, always a string).
      // NOTE 2 (2026-07-18): DO NOT `encodeURIComponent` here — TanStack Router's
      // search-param serializer already encodes reserved characters. Double-encoding
      // turned `returnTo=/login` into `%252Flogin`, which the login page then read
      // as the literal string `%2Flogin` and tried to navigate() to, producing the
      // 404 URL `/%2Flogin`. Passing the raw path lets the router encode once.
      // Capture the protected route only once. During navigation this component
      // can render again with location.pathname === "/login" before it unmounts;
      // recomputing here would overwrite the intended destination.
      returnToRef.current ??= location.pathname + window.location.search;
      void navigate({
        to: "/login",
        search: { returnTo: returnToRef.current } as never,
        replace: true,
      });
      return;
    }
    if (!hasRole(user, persona.role)) {
      void navigate({ to: "/", replace: true });
    }
  }, [loading, user, persona.role, navigate, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">
        <div className="animate-pulse text-sm">Checking your session…</div>
      </div>
    );
  }
  if (!user || !hasRole(user, persona.role)) return null;

  return <>{children}</>;
}
